"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mic, Briefcase, Lock, MessageSquare, ArrowRight, Sparkles, Clock } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Button } from "@/components/ui/button";

const PATHS = [
  {
    icon: Mic,
    title: "Speaking & Keynotes",
    body: "Nobel Peace Prize, WEF, and beyond. Tailored keynotes drawn from 15+ years of sovereign-scale operations.",
    gradient: "from-purple/20 to-purple/0",
    accent: "text-purple",
    iconBg: "bg-gradient-royal",
    href: "/speaking/",
  },
  {
    icon: Briefcase,
    title: "Executive Advisory & Boards",
    body: "Operational and governance advisory for founders, executives, and institutions. Board service.",
    gradient: "from-emerald/20 to-emerald/0",
    accent: "text-emerald",
    iconBg: "bg-gradient-success",
    href: "/services/executive-advisory/",
  },
  {
    icon: Lock,
    title: "Operational Engagements",
    body: "Discreet, high-stakes work for governments and leaders. By introduction only.",
    gradient: "from-cyan/20 to-cyan/0",
    accent: "text-cyan",
    iconBg: "bg-gradient-clarity",
    href: "/services/operational-consulting/",
  },
  {
    icon: MessageSquare,
    title: "Press & Media",
    body: "Expert commentary for journalists on operational excellence, women in leadership, and governance.",
    gradient: "from-amber-gold/20 to-amber-gold/0",
    accent: "text-amber-gold",
    iconBg: "bg-gradient-premium",
    href: "/services/press-media-commentary/",
  },
];

export function StrategicAccelerationSection() {
  return (
    <Section
      id="strategic-acceleration"
      ariaLabel="Strategic Acceleration Zone"
      background="default"
      className="py-20 md:py-28"
    >
      {/* Multi-color background blobs — NOT orange */}
      <div aria-hidden className="absolute inset-0 -m-10 rounded-3xl bg-gradient-royal opacity-[0.06] blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-emerald/8 blur-3xl pointer-events-none" />

      <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 border border-border/30">
        <SectionHeader
          icon={Sparkles}
          overline="Strategic Acceleration"
          overlineClassName="text-amber-gold"
          pillClassName="glass-tinted-amber-gold border-amber-gold/20"
          title={
            <>
              Engagements Available <span className="text-gradient-royal">By Introduction.</span>
            </>
          }
          subtitle="Strategic opportunities have timelines. Market windows close. Competitive advantages erode. Crises escalate. The cost of delay often exceeds the cost of action."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          {PATHS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <Link href={p.href} className="block h-full group">
                <div
                  className={`relative glass-card rounded-2xl p-5 h-full overflow-hidden bg-gradient-to-br ${p.gradient} border border-border/30`}
                >
                  <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                  <div className="relative flex items-start gap-4">
                    <div
                      className={`size-12 rounded-xl ${p.iconBg} flex items-center justify-center shadow-md shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-all`}
                    >
                      <p.icon className="size-6 text-white" />
                    </div>
                    <div>
                      <h3 className={`text-heading-md mb-2 ${p.accent}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTAs — three different gradient colors (no single orange dominance) */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button
            asChild
            size="lg"
            className="h-14 px-7 text-base bg-gradient-royal shadow-lg shadow-purple/30"
          >
            <Link href="/contact/">
              <Sparkles className="size-5" />
              Book Dr. Jyoti Kush
              <ArrowRight className="size-5" />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="h-14 px-7 text-base glass border-emerald/30 hover:border-emerald/60"
          >
            <Link href="/contact/">
              <Briefcase className="size-5 text-emerald" />
              Inquire About Advisory
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="ghost"
            className="h-14 px-7 text-base hover:bg-muted/50"
          >
            <Link href="/services/press-media-commentary/">
              <MessageSquare className="size-5 text-amber-gold" />
              Press & Media
            </Link>
          </Button>
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 text-caption text-muted-foreground">
            <Clock className="size-3" />
            <span>The cost of delay often exceeds the cost of action.</span>
          </div>
        </div>

        {/* Engagement modalities — 6 distinct non-pink colors */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          {[
            { cls: "text-lime", label: "Discreet" },
            { cls: "text-indigo", label: "Sovereign-grade" },
            { cls: "text-denim", label: "Confidential" },
            { cls: "text-cyan", label: "By introduction" },
            { cls: "text-amber-gold", label: "Verified outcomes" },
            { cls: "text-emerald", label: "Sovereign discretion" },
          ].map((s) => (
            <span
              key={s.label}
              className="glass rounded-full px-3 py-1.5 text-caption font-medium"
            >
              <span className={s.cls}>·</span>{" "}
              <span className="text-muted-foreground ml-1">{s.label}</span>
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
}
